/**
 * Utility functions for handling and formatting error messages
 */

/**
 * Map of HTTP status codes to user-friendly error messages
 */
const ERROR_MESSAGES = {
  // Client errors (4xx)
  400: 'Invalid request. Please check your input and try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  405: 'This action is not allowed.',
  408: 'Request timeout. Please check your connection and try again.',
  409: 'A conflict occurred. The data may have been modified by someone else.',
  410: 'This resource is no longer available.',
  422: 'Unable to process your request. Please check the data and try again.',
  429: 'Too many requests. Please wait a moment and try again.',

  // Server errors (5xx)
  500: 'A server error occurred. Please try again later.',
  501: 'This feature is not currently available.',
  502: 'Service temporarily unavailable. Please try again in a moment.',
  503: 'Service is currently under maintenance. Please try again later.',
  504: 'The server took too long to respond. Please try again.',
};

/**
 * Converts an error object to a user-friendly error message
 *
 * @param {Error|Object} error - The error object from axios or other sources
 * @param {string} defaultMessage - Default message if no specific message is found
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error, defaultMessage = 'An unexpected error occurred. Please try again.') => {
  // If error is already a string, return it
  if (typeof error === 'string') {
    return error;
  }

  // Check if error has a response (axios error)
  if (error?.response) {
    const status = error.response.status;
    const data = error.response.data;

    // Priority 1: Check for detail field in response data (common in Django REST Framework)
    if (data?.detail) {
      return data.detail;
    }

    // Priority 2: Check for error field in response data
    if (data?.error) {
      return data.error;
    }

    // Priority 3: Check for message field in response data
    if (data?.message) {
      return data.message;
    }

    // Priority 4: Check for non_field_errors (Django REST Framework validation errors)
    if (data?.non_field_errors && Array.isArray(data.non_field_errors)) {
      return data.non_field_errors.join(' ');
    }

    // Priority 5: Check for field-specific validation errors
    if (data && typeof data === 'object') {
      const fieldErrors = [];
      for (const [field, errors] of Object.entries(data)) {
        if (Array.isArray(errors)) {
          fieldErrors.push(`${field}: ${errors.join(', ')}`);
        } else if (typeof errors === 'string') {
          fieldErrors.push(`${field}: ${errors}`);
        }
      }
      if (fieldErrors.length > 0) {
        return fieldErrors.join('; ');
      }
    }

    // Priority 6: Use predefined message for status code
    if (ERROR_MESSAGES[status]) {
      return ERROR_MESSAGES[status];
    }

    // Priority 7: Generic message based on status code category
    if (status >= 500) {
      return 'A server error occurred. Please try again later.';
    }
    if (status >= 400) {
      return 'Request failed. Please check your input and try again.';
    }
  }

  // Check if error has request property (network error)
  if (error?.request && !error?.response) {
    return 'Network error. Please check your internet connection and try again.';
  }

  // Check for error message property
  if (error?.message) {
    // Don't show technical axios messages to users
    if (error.message.includes('Network Error')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (error.message.match(/timeout/i)) {
      return 'Request timeout. Please try again.';
    }
    // Avoid showing "Request failed with status code XXX" messages
    if (error.message.match(/Request failed with status code (\d+)/)) {
      const statusMatch = error.message.match(/Request failed with status code (\d+)/);
      const status = parseInt(statusMatch[1]);
      return ERROR_MESSAGES[status] || defaultMessage;
    }

    // If it's a custom error message (not from axios), show it
    if (!error.message.includes('status code') && !error.message.includes('Network Error')) {
      return error.message;
    }
  }

  return defaultMessage;
};

/**
 * Checks if an error is an authentication error (401 or 403)
 *
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if it's an auth error
 */
export const isAuthError = (error) => {
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

/**
 * Checks if an error is a network error
 *
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if it's a network error
 */
export const isNetworkError = (error) => {
  return error?.request && !error?.response;
};

/**
 * Checks if an error is a validation error (400 or 422)
 *
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if it's a validation error
 */
export const isValidationError = (error) => {
  const status = error?.response?.status;
  return status === 400 || status === 422;
};

/**
 * Gets the HTTP status code from an error
 *
 * @param {Error|Object} error - The error object
 * @returns {number|null} The status code or null if not found
 */
export const getStatusCode = (error) => {
  return error?.response?.status || null;
};

